import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from './authConfig';
import ParkingTable from './components/ParkingTable';
import DefaultOwnerParkingTable from './components/DefaultOwnerParkingTable';
import ParkingSlotManager from './components/ParkingSlotManager';
import './App.css';

function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [powerUserMode, setPowerUserMode] = useState(false);
  const [currentView, setCurrentView] = useState('reservations'); // 'reservations' or 'slots'

  // Reset to reservations view when PowerUser mode is turned off
  useEffect(() => {
    if (!powerUserMode && currentView === 'slots') {
      setCurrentView('reservations');
    }
  }, [powerUserMode, currentView]);

  useEffect(() => {
    if (isAuthenticated && accounts.length > 0) {
      const account = accounts[0];
      // Check if user has PowerUser role
      const roles = account.idTokenClaims?.roles || [];
      const isPowerUser = roles.includes('PowerUser');

      setUser({
        name: account.name,
        user_id: account.username,
        family_name: account.idTokenClaims?.family_name || account.name?.split(' ').pop() || 'User',
        isPowerUser: isPowerUser,
        roles: roles,
      });
    }
  }, [isAuthenticated, accounts]);

  const handleLogin = () => {
    instance.loginPopup(loginRequest).catch((error) => {
      console.error('Login failed:', error);
    });
  };

  const handleLogout = () => {
    // App-only logout: clear all local storage without redirecting to Azure
    try {
      // Clear entire localStorage
      localStorage.clear();
      
      // Clear entire sessionStorage
      sessionStorage.clear();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    
    // Clear any additional app state
    setUser(null);
    setPowerUserMode(false);
    
    // Navigate to root to force MSAL re-initialization
    window.location.href = window.location.origin;
  };

  if (!isAuthenticated) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>🚗 Parking Slot Reservation</h1>
          <p>Please sign in with your Azure account to continue</p>
          <button onClick={handleLogin} className="login-button">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="user-info-row">
          <div className="user-info-item">
            <span>Welcome, {user?.name}</span>
          </div>
          {user?.isPowerUser && (
            <div className="user-info-item">
              <label className="power-user-toggle-header">
                <input
                  type="checkbox"
                  checked={powerUserMode}
                  onChange={(e) => setPowerUserMode(e.target.checked)}
                />
                <span className="toggle-slider-header"></span>
                <span className="toggle-label-header">💪 PowerUser</span>
              </label>
            </div>
          )}
          <div className="user-info-item">
            <button onClick={handleLogout} className="logout-button">
              Sign Out
            </button>
          </div>
        </div>
        <h1>🚗 Aller Media Parking Slot Reservation, Helsinki</h1>
      </header>
      {user?.isPowerUser && powerUserMode && (
        <nav className="app-nav">
          <button
            className={`nav-button ${currentView === 'reservations' ? 'active' : ''}`}
            onClick={() => setCurrentView('reservations')}
          >
            📅 Reservations
          </button>
          <button
            className={`nav-button ${currentView === 'slots' ? 'active' : ''}`}
            onClick={() => setCurrentView('slots')}
          >
            ⚙️ Manage Slots
          </button>
        </nav>
      )}
      <main className="app-main">
        {currentView === 'reservations' ? (
          <>
            <ParkingTable user={user} powerUserMode={powerUserMode} msalInstance={instance} />
            <DefaultOwnerParkingTable user={user} powerUserMode={powerUserMode} msalInstance={instance} />
          </>
        ) : (
          <ParkingSlotManager user={user} powerUserMode={powerUserMode} msalInstance={instance} />
        )}
      </main>
    </div>
  );
}

export default App;
